import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Close as CloseIcon,
  WarningAmber as WarningIcon
} from '@mui/icons-material';
import {
  IconButton,
  Paper
} from '@mui/material';

export type AlertNotificationItem = {
  connectionStatus?: string;
  deviceName: string;
  id: string;
  programStatus: string;
};

type AlertNotificationProps = {
  alerts: AlertNotificationItem[];
};

type AlertNotificationState = {
  queued: AlertNotificationItem[];
  visible: AlertNotificationItem[];
};

/*
 * Alert Notification UI settings.
 *
 * Keep these values together so the size, spacing and display behavior
 * can be adjusted easily during the final UI tuning phase.
 */
export const ALERT_NOTIFICATION_WIDTH = 480;
const ALERT_HEIGHT = 180;
const ALERT_GAP = 12;
export const ALERT_NOTIFICATION_RIGHT_OFFSET = 24;
const ALERT_BOTTOM_OFFSET = 24;

const ALERT_DISPLAY_TIME_MS = 5000;
const MAX_VISIBLE_ALERTS = 4;

const ALERT_ACCENT_COLOR = '#d32f2f';
const ALERT_VALUE_COLOR = '#d32f2f';

type AlertCardProps = {
  alert: AlertNotificationItem;
  onClose: (id: string) => void;
};

const AlertCard = ({
  alert,
  onClose
}: AlertCardProps) => {
  useEffect(() => {
    const timer = window.setTimeout(() => {
      onClose(alert.id);
    }, ALERT_DISPLAY_TIME_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [alert.id, onClose]);

  return (
    <Paper
      elevation={4}
      style={{
        backgroundColor: '#fff',
        borderLeft: `4px solid ${ALERT_ACCENT_COLOR}`,
        boxSizing: 'border-box',
        flexShrink: 0,
        height: `${ALERT_HEIGHT}px`,
        overflow: 'hidden',
        padding: '20px 20px 18px 20px',
        position: 'relative',
        width: `${ALERT_NOTIFICATION_WIDTH}px`
      }}
    >
      <IconButton
        aria-label={`Close alert for ${alert.deviceName}`}
        size="small"
        style={{
          position: 'absolute',
          right: '8px',
          top: '8px'
        }}
        onClick={() => onClose(alert.id)}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <div
        style={{
          alignItems: 'center',
          display: 'flex',
          paddingRight: '32px'
        }}
      >
        <WarningIcon
          style={{
            color: ALERT_ACCENT_COLOR,
            marginRight: '10px'
          }}
        />

        <div
          title={alert.deviceName}
          style={{
            fontSize: '18px',
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {alert.deviceName}
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gap: '10px',
          marginTop: '24px'
        }}
      >
        {alert.connectionStatus && (
          <div
            style={{
              alignItems: 'center',
              display: 'grid',
              gridTemplateColumns: '110px 1fr'
            }}
          >
            <span>Connection:</span>

            <span
              style={{
                color: ALERT_VALUE_COLOR,
                fontWeight: 600
              }}
            >
              {alert.connectionStatus}
            </span>
          </div>
        )}

        <div
          style={{
            alignItems: 'center',
            display: 'grid',
            gridTemplateColumns: '110px 1fr'
          }}
        >
          <span>Program:</span>

          <span
            style={{
              color: ALERT_VALUE_COLOR,
              fontWeight: 600
            }}
          >
            {alert.programStatus}
          </span>
        </div>
      </div>
    </Paper>
  );
};

const AlertNotification = ({
  alerts
}: AlertNotificationProps) => {
  const processedAlertIdsRef = useRef<Set<string>>(
    new Set()
  );

  const [alertState, setAlertState] =
    useState<AlertNotificationState>({
      visible: [],
      queued: []
    });

  /*
   * Receive only alerts that have not already been processed.
   *
   * The AlertNotification component does not inspect or recalculate
   * device status. Each item is treated as an immutable snapshot of
   * the state at the time the alert was generated.
   */
  useEffect(() => {
    const newAlerts = alerts
      .filter(
        alert => !processedAlertIdsRef.current.has(alert.id)
      )
      .map(alert => ({
        ...alert
      }));

    if (newAlerts.length === 0) {
      return;
    }

    newAlerts.forEach(alert => {
      processedAlertIdsRef.current.add(alert.id);
    });

    setAlertState(current => {
      const availableSlots = Math.max(
        0,
        MAX_VISIBLE_ALERTS - current.visible.length
      );

      const immediatelyVisible =
        newAlerts.slice(0, availableSlots);

      const waiting =
        newAlerts.slice(availableSlots);

      return {
        visible: [
          ...current.visible,
          ...immediatelyVisible
        ],
        queued: [
          ...current.queued,
          ...waiting
        ]
      };
    });
  }, [alerts]);

  /*
   * Remove one visible alert.
   *
   * If another alert is waiting in the FIFO queue, move the oldest
   * queued alert into the newly available display slot immediately.
   */
  const handleClose = useCallback((alertId: string) => {
    setAlertState(current => {
      const nextVisible = current.visible.filter(
        alert => alert.id !== alertId
      );

      if (nextVisible.length === current.visible.length) {
        return current;
      }

      const nextAlert = current.queued[0];

      if (!nextAlert) {
        return {
          visible: nextVisible,
          queued: []
        };
      }

      return {
        visible: [
          ...nextVisible,
          nextAlert
        ],
        queued: current.queued.slice(1)
      };
    });
  }, []);

  if (alertState.visible.length === 0) {
    return null;
  }

  return (
    <div
      style={{
        bottom: `${ALERT_BOTTOM_OFFSET}px`,
        display: 'flex',
        flexDirection: 'column-reverse',
        gap: `${ALERT_GAP}px`,
        position: 'fixed',
        right: `${ALERT_NOTIFICATION_RIGHT_OFFSET}px`,
        zIndex: 1500
      }}
    >
      {alertState.visible.map(alert => (
        <AlertCard
          key={alert.id}
          alert={alert}
          onClose={handleClose}
        />
      ))}
    </div>
  );
};

export default AlertNotification;